<template>
    <div>
        <b-table hover
                 outlined
                 striped
                 show-empty
                 tbody-class="bg-white"
                 thead-tr-class="bg-secondary"
                 class="text-center mb-5"
                 selected-variant="success"
                 id="table"
                 ref="inventoryTable"
                 :tbody-tr-class="activeRowClass"
                 :busy.sync="loading"
                 :sort-by.sync="sortBy"
                 :sort-desc.sync="sortDesc"
                 :per-page="perPage"
                 :items="orders"
                 :current-page="currentPage"
                 :fields="parseFields()">

            <template v-slot:thead-top="data">
                <b-tr class="text-left bg-dark">
                    <b-th :colspan="parseFields().length - (tableName.status === 'second-return-of-returns' ? 3 : 2)">
                        <span class="h4">
                            <span v-if="tableName.method">{{ tableName.method | titleCase }} </span>
                            {{ tableName.status.replace('-', ' ') | titleCase }}
                            <span v-if="tableName.status === 'return-of-returns' ">(1st Returns)</span>
                        </span>
                    </b-th>

                    <b-th class="text-center">
                        <span v-if="presetLocation">
                            <b-form-select style="max-width: 200px" v-model="presetLocation"
                                           :options="safe_options"></b-form-select>
                        </span>
                        <span v-else>
                            <i class="fa-solid fa-info-circle fa-fw"></i> <small>Scan a location to set default.</small>
                        </span>
                    </b-th>
                    <b-th :colspan="tableName.status === 'second-return-of-returns' ? 2 : 1">
                        <div class="text-right">
                            <b-button :disabled="downloadingCSV" @click="download" variant="info"><i
                                v-if="downloadingCSV" class="fa-regular fa-spin fa-spinner"></i><i v-else
                                                                                           class="fa-solid fa-file-export"></i>
                                Export to Excel
                            </b-button>
                        </div>
                    </b-th>
                </b-tr>
            </template>
            <!--loading-->
            <template v-slot:table-busy>
                <LoadingSpinner :busy="loading"/>
            </template>

            <!--verified-->
            <template v-slot:cell(verified)="data">
                <template v-if="data.item.saving"><i class="fa-regular fa-spinner fa-spin fa-lg"></i></template>
                <template v-else-if="data.item.loading"><i class="fa-regular fa-file-import fa-lg fade-in-left"></i></template>
                <template v-else-if="data.item.verified"><i class="fa-solid fa-check text-success fa-lg"></i>
                </template>
            </template>

            <!--id-->
            <template v-slot:cell(id)="data">
                <a :href="'/kit/view/' + data.item.id">{{ data.item.id }}</a>
            </template>

            <!--paid date-->
            <template v-slot:cell(paid_at)="data">{{ data.item.paid_at | datetime }}</template>

            <!--received_at-->
            <template v-slot:cell(received_at)="data">{{ data.item.received_at | datetime }}</template>

            <!--payment_type-->
            <template v-slot:cell(payment_type)="data">{{ data.item.payments[0].payment_type | titleCase }}</template>

            <!-- appraised_at -->
            <template v-slot:cell(appraised_at)="data">{{ data.item.appraised_at | datetime }}</template>

            <!--name-->
            <template v-slot:cell(name)="data">{{ data.item.full_name }}</template>

            <!--flags-->
            <template v-slot:cell(flags)="data">
                <template v-if="data.item.flags">
                    <b-button :id=" 'flag-' + data.index " variant="link">
                        <i class="fa-solid fa-flag-checkered text-warning"></i>
                    </b-button>

                    <b-popover :target="`flag-${data.index}`" title="Flags" triggers="click blur" placement="bottom">

                        <template v-for="(flag, index) in data.item.flags">
                            <div :key="index" class="mb-1">
                                <b-badge pill>{{ flag.flag_type.name }}</b-badge>
                            </div>
                        </template>
                    </b-popover>
                </template>
                <template v-else>-</template>
            </template>

            <!--safe-->
            <template v-slot:cell(safe)="data">
                <b-form-select style="max-width: 200px" v-model="data.item.location_id" @change="save(data.item)"
                               :options="safe_options" :disabled="data.item.saving"></b-form-select>
            </template>

            <!--large kit-->
            <template v-slot:cell(large_kit)="data">
                <b-form-checkbox v-model="data.item.large_kit" name="check-button" switch :disabled="data.item.saving"
                                 @change="saveWithCageOption(data.item, data.item.large_kit)"></b-form-checkbox>
                <small @click="setCageWork(data.item)" v-if="!data.item.isEditingCage && !data.item.cage_id && data.item.large_kit">No Cage</small>
                <small @click="setCageWork(data.item)" v-else-if="!data.item.isEditingCage && data.item.cage_id">Cage {{data.item.cage_id}}</small>
                <b-form-select v-model="data.item.cage_id"
                               v-if="data.item.isEditingCage"
                               @change="saveCage(data.item)"
                               :options="cage_options"></b-form-select>
                <i @click="cancelCageWork(data.item)"
                   v-if="data.item.isEditingCage"
                   class="fa-solid fa-times text-danger pointer">
                </i>
            </template>

            <!--2nd Return Actions-->
            <template v-slot:cell(actions)="data">
                <b-button size="sm" variant="warning" v-b-tooltip.hover title="Move to Batch"
                          @click="markForBatch(data.item)"
                          :disabled="data.item.saving || (data.item.savingBatch && data.item.saved)">
                    <i class="fa-solid fa-spinner fa-spin fa-fw" v-if="data.item.savingBatch && data.item.saving"></i>
                    <i class="fa-solid fa-check fa-fw" v-else-if="data.item.savingBatch && data.item.saved"></i>
                    <i class="fa-solid fa-industry fa-fw" v-else></i>
                </b-button>
                <b-button size="sm" variant="info" v-b-tooltip.hover title="Mark for Return"
                          @click="markForReturn(data.item)"
                          :disabled="data.item.saving || (data.item.savingReturn && data.item.saved)">
                    <i class="fa-solid fa-spinner fa-spin fa-fw" v-if="data.item.savingReturn && data.item.saving"></i>
                    <i class="fa-solid fa-check fa-fw" v-else-if="data.item.savingReturn && data.item.saved"></i>
                    <i class="fa-solid fa-reply fa-fw" v-else></i>
                </b-button>
            </template>

            <!--footer-->
            <template v-slot:custom-foot="data">
                <b-tr class="bg-secondary">
                    <b-th :colspan="parseFields().length">
                        <b-pagination
                            class="mb-0"
                            align="center"
                            aria-controls="table"
                            v-model="currentPage"
                            :total-rows="rows"
                            :per-page="perPage">
                        </b-pagination>
                    </b-th>
                </b-tr>
            </template>
        </b-table>

    </div>
</template>

<script>
import LoadingSpinner from '@/components/LoadingSpinner';
import _ from 'lodash';
import moment from 'moment';

export default {
    name: 'inventory-list',
    title: 'Inventory List',
    components: {
        LoadingSpinner,
    },
    props: ['status'],
    data() {
        return {
            downloadingCSV: false,
            sortBy: null,
            sortDesc: true,
            perPage: 15,
            currentPage: 1,
            loading: false,
            orders: [],
            selected: [],
            activeOrder: null,
            fields: [{key: 'verified', label: 'Saved'}, {key: 'id', label: 'Order #', sortable: true}, 'name', 'flags', 'safe', 'large_kit'],
            dynamicFields: [],
            safe_options: [
                {value: null, text: 'Select Location', disabled: true},
                {value: 1, text: 'Pre-Regulatory Hold 1'},
                {value: 2, text: 'Pre-Regulatory Hold 2'},
                {value: 3, text: 'Regulatory Hold 1'},
                {value: 4, text: 'Regulatory Hold 2'},
                {value: 5, text: 'Luxury Safe'},
                {value: 6, text: 'Inventory Management Room'},
                {value: 7, text: 'Processing Floor'},
            ],
            cage_options: [
                {value: null, text: 'Select Cage', disabled: true},
                {value: 1, text: 'Cage 1'},
                {value: 2, text: 'Cage 2'},
                {value: 3, text: 'Cage 3'},
                {value: 4, text: 'Cage 4'},
                {value: 5, text: 'Cage 5'},
                {value: 6, text: 'Cage 6'},
            ],
            presetLocation: null,
        };
    },
    mounted() {
        this.getData();

        if(this.tableName.status === 'paid-kits') {
            //this.sortBy = 'appraised_at';
        } else {
            this.sortBy = null;
        }

        this.$barcodeScanner.init(rawBarcode => {
            this.$barcodeScanner.parseBarcode(rawBarcode, (barcode, context) => {
                document.activeElement.blur();
                // this.$bvToast.toast('Scanned barcode "' + barcode + '"', {
                //     title: 'Barcode Scanner',
                //     appendToast: true,
                //     autoHideDelay: 3000,
                // });
                let lookupField = null;
                let lookupEndpoint = null;
                let lookupBarcode = null;
                switch(context) {
                    case 'SL':
                        if (this.activeOrder) {
                            let order = _.find(this.orders, ['id', this.activeOrder]);
                            if (order) {
                                order.location_id = parseInt(barcode);
                                order.verified = true;
                                this.save(order).then(() => {
                                    this.activeOrder = null;
                                });
                            } else {
                                this.activeOrder = null;
                            }
                        } else {
                            this.presetLocation = parseInt(barcode);
                        }
                        break;
                    default://returned scan in, most code is shared w/order except for lookup in current list and get methods.
                        //setup fields
                        if (context === 'O' || context === 'R') { //order scan in, code is used by return above
                            lookupEndpoint = '/inventory_management/getOrder/';
                            lookupField = 'id';
                            lookupBarcode = parseInt(barcode);
                        } else {
                            lookupEndpoint = '/inventory_management/getOrderByTracking/';
                            lookupField = 'return_tracking_number';
                            lookupBarcode = barcode;
                        }

                        if (true) {
                            // First check to see if the order is already on the list
                            //let order = _.find(this.orders, [lookupField, lookupBarcode]);
                            let order = _.find(this.orders, (order) => {
                                if (order[lookupField] == lookupBarcode) {
                                    return true;
                                }
                                else if(_.findIndex(order.tracking_numbers, ['tracking_number', lookupBarcode]) != -1) {
                                    return true;
                                }
                                console.log(order);
                                return false;
                            });

                            // If not found, add the record and make a call to the API to load the data
                            if (order) {
                                this.activeOrder = order.id;
                                if (this.presetLocation) {
                                    order.location_id = this.presetLocation;
                                    order.verified = true;
                                    this.save(order).then(() => {
                                        this.activeOrder = null;
                                    });
                                } else {
                                    this.$bvToast.toast('Select a default safe location first for faster editing.', {
                                        title: 'Select Default Safe',
                                        variant: 'info',
                                        appendToast: true,
                                        autoHideDelay: 5000,
                                    });
                                }
                            } else if (this.tableName.status === 'return-of-returns' || this.tableName.status === 'second-return-of-returns' || this.tableName.status === 'edit-safe') {
                                let order = {
                                    id: null,
                                    full_name: null,
                                    location_id: null,
                                    large_kit: false,
                                    verified: false,
                                    flags: null,
                                    received_at: null,
                                    appraised_at: null,
                                    paid_at: null,
                                    return_tracking_number: null,
                                    loading: true,
                                };
                                //access the correct property for this context, and apply it (return is return_tracking number, order is id, ect).
                                order[lookupField] = lookupBarcode;
                                //should be reference copy, not deep copy, no need to refetch to update in array.
                                this.orders.push(order);
                                this.$http.get(lookupEndpoint + barcode)
                                    .then((result) => {
                                        let orderData = result.data;
                                        if (orderData && orderData.customer) { //prevent js error by empty order reference, (return tracking will not be an order id).
                                            order.id = orderData.id;
                                            order.full_name = orderData.customer.full_name;
                                            order.flags = orderData.flags;
                                        } else {
                                            this.orders.pop();
                                            this.$bvModal.msgBoxOk('Order not found with barcode: ' + barcode + '. Please check the status of the order to confirm it qualifies for ' + this.titleCase(this.tableName), {
                                                title: 'Not Found',
                                                size: 'md',
                                                buttonSize: 'sm',
                                                okVariant: 'danger',
                                                headerClass: 'p-2 border-bottom-0 text-center d-block',
                                                footerClass: 'p-2 border-top-0',
                                            });
                                            return;
                                        }
                                        //order.loading = false;
                                        this.activeOrder = order.id;
                                        if (this.presetLocation) {
                                            order.verified = true;
                                            order.location_id = this.presetLocation;
                                            this.save(order).then(() => {
                                                this.activeOrder = null;
                                            })
                                            .finally(() => {
                                                order.loading = false;
                                            });
                                        } else {
                                            order.loading = false;
                                        }
                                    })
                                    .catch(() => {
                                        order.loading = false;
                                        this.orders.pop();
                                        this.$bvModal.msgBoxOk('Order not found with barcode: ' + barcode + '. Please check the status of the order to confirm it qualifies for ' + this.titleCase(this.tableName.status), {
                                            title: 'Not Found',
                                            size: 'md',
                                            buttonSize: 'sm',
                                            okVariant: 'danger',
                                            headerClass: 'p-2 border-bottom-0 text-center d-block',
                                            footerClass: 'p-2 border-top-0',
                                        });
                                    });
                            } else {
                                this.$bvModal.msgBoxOk('Order not found with barcode: ' + barcode + '. Please check the status of the order to confirm it qualifies for ' + this.titleCase(this.tableName.status), {
                                    title: 'Not Found',
                                    size: 'md',
                                    buttonSize: 'sm',
                                    okVariant: 'danger',
                                    headerClass: 'p-2 border-bottom-0 text-center d-block',
                                    footerClass: 'p-2 border-top-0',
                                });
                            }
                        } else {
                            //todo message signifying that order/return/ect scanning is not allowed on this page.
                        }
                        break;
                }
            });
        });

        if (this.$route.query.scan) {
            this.$barcodeScanner.simulateBarcode(this.$route.query.scan);
            this.$router.replace({'query': null});
        }
    },
    beforeDestroy() {
        this.$barcodeScanner.destroy();
    },
    computed: {
        tableName() {
            return this.$route.params;
        },
        rows() {
            return this.orders.length;
        },
    },
    methods: {
        // sortBy() {
        //     let sortBy;
        //     if(this.tableName.status === 'paid-kits') {
        //         sortBy = 'appraised_at';
        //     } else {
        //         sortBy = 'id';
        //     }
        //     return sortBy;
        // },
        titleCase(str) {
            str = str.replaceAll('-', ' ');
            str = str.toLowerCase().split(' ');
            for (var i = 0; i < str.length; i++) {
                str[i] = str[i].charAt(0).toUpperCase() + str[i].slice(1);
            }
            return str.join(' ');
        },
        parseFields() {
            let copy = _.cloneDeep(this.fields);
            switch (this.tableName.status) {
                case 'paid-kits':
                    copy.splice(1, 0,
                            {key: 'paid_at', label: 'Paid Date', sortable: true},
                            {key: 'appraised_at', label: 'Appraisal Date', sortable: true},
                            {key: 'payment_type', label: 'Payment Type', sortable: true});
                    break;
                case 'outstanding-kits':
                    copy.splice(1, 0, {key: 'received_at', label: 'Received Date'});
                    break;
                case 'offers-generated':
                    copy.splice(1, 0, {key: 'received_at', label: 'Received Date'});
                    copy.splice(2, 0, {key: 'appraised_at', label: 'Appraisal Date'});
                    break;
                case 'returns':
                    copy.splice(1, 0, {key: 'appraised_at', label: 'Appraisal Date'});
                    break;
                case 'second-return-of-returns':
                    copy.splice(6, 0, {key: 'actions', label: 'Actions'});
                    break;
            }
            return copy;
        },
        activeRowClass(item) {
            if (item && item.id === this.activeOrder) {
                return 'table-success';
            }

            if (item && (this.tableName.status === 'outstanding-kits' || this.tableName.status === 'return-of-returns' || this.tableName.status === 'second-return-of-returns')) {
                if (item.location_id === 7 && item.verified) {
                    return 'table-info';
                } else if (this.tableName.status === 'outstanding-kits' && item.received_at && !moment(item.received_at).isSame(moment(), 'day')) {
                    return 'table-warning';
                } else if (this.tableName.status === 'second-return-of-returns') {
                    let flag = _.find(item.flags, ['flag_type_id', 13]);
                    if (flag && moment(flag.created_at).isBefore(moment().subtract(120, 'days'), 'days')) {
                        return 'table-danger';
                    } else if (flag && moment(flag.created_at).isBefore(moment().subtract(85, 'days'), 'days')) {
                        return 'table-warning';
                    }
                }
            }
            //
            if (
                item &&
                item.verified
                && [
                    'return-of-returns',
                    'second-return-of-returns'
                ].indexOf(this.tableName.status) == -1
            ) {
                return 'table-success';
            }
        },

        markForReturn(item) {
            item.savingReturn = true;
            this.$forceUpdate();
            item.location_id = 7;
            this.save(item, true);
        },

        markForBatch(item) {
            item.savingBatch = true;
            item.saving = true;
            this.$forceUpdate();
            this.$http.put('/orders/' + item.id, {status_id: 13, override_inventory: 1})
                .then(() => {
                    item.saving = false;
                    item.saved = true;
                    item.verified = true;
                    this.$forceUpdate();
                    setTimeout(() => {
                        this.orders = this.orders.filter((element) => {
                            return element !== item;
                        });
                    }, 3000);
                })
                .catch(() => {
                    item.saving = false;
                    this.$forceUpdate();
                });
        },
        async saveWithCageOption(item, largeKitStatus) {
            item.isEditingCage = largeKitStatus;
            if (!largeKitStatus) {
                this.save(item, false);
            }
            this.$forceUpdate();
        },
        async setCageWork(item) {
            item.isEditingCage = !item.isEditingCage;
            this.$forceUpdate();
        },
        async cancelCageWork(item) {
            this.setCageWork(item);
            this.saveCage(item);
        },
        async saveCage(item) {
            item.isEditingCage = false;
            this.save(item);
            this.$forceUpdate();
        },
        async save(item, remove) {
            if (!remove) {
                remove = false;
            }
            document.activeElement.blur();
            if (item.location_id) {
                item.saving = true;
                this.$forceUpdate();
                let payload = item;
                payload.list_name = this.tableName.status + (this.tableName.method ? '-' + this.tableName.method : '');
                await this.$http.post('/inventory_management/' + item.id, payload)
                    .then(() => {
                        item.saving = false;
                        item.saved = true;
                        if (item.location_id) {
                            item.verified = true;
                        }
                        this.$forceUpdate();
                        if (remove) {
                            setTimeout(() => {
                                this.orders = this.orders.filter((element) => {
                                    return element !== item;
                                });
                            }, 3000);
                        }
                    });
            }
        },
        download() {
            this.downloadingCSV = true;
            let list_name = this.tableName.status + (this.tableName.method ? '-' + this.tableName.method : '');
            this.$http.get('/inventory_management/' + list_name + '?asXLSL=1', {
                responseType: 'blob',
            }).then((response) => {
                let fileDate = new Date().toISOString();
                fileDate
                    .replace(/T/, '-') // Replace 'T' with a hyphen
                    .replace(/:/g, '-') // Replace all colons with hyphens
                    .replace(/\./, '-') // Replace the first period with a hyphen (for milliseconds)
                    .replace(/Z/, ''); // Remove 'Z' (Zulu time indicator)
                this.$http.downloadFile(response.data, 'InventoryManagementReport_' + list_name + '_' + fileDate + '.csv');
            }).finally(() => {
                this.downloadingCSV = false;
            });
        },
        getData() {
            this.loading = true;
            this.activeOrder = null;
            this.presetLocation = null;
            this.orders = [];

            let list_name = this.tableName.status + (this.tableName.method ? '-' + this.tableName.method : '');

            this.$http.get('/inventory_management/' + list_name)
                .then(response => {
                    let orders = response.data;
                    _.each(orders, order => {
                        this.orders.push({
                            payments: order.payments,
                            id: order.id,
                            full_name: order.customer.full_name,
                            location_id: order.inventory_management[0] ? order.inventory_management[0].location_id : null,
                            large_kit: order.inventory_management[0] ? order.inventory_management[0].large_kit : false,
                            verified: !!(order.inventory_management[0] && order.inventory_management[0].list_name === list_name && order.inventory_management[0].location_id !== null && order.inventory_management[0].user_id),
                            flags: order.flags,
                            received_at: order.received_at,
                            appraised_at: order.appraised_at,
                            tracking_numbers: order.tracking_numbers,
                            cage_id: order.inventory_management[0] ? order.inventory_management[0].cage_id : false,
                            paid_at: (order.payments && order.payments[0]) ? order.payments[0].processed_at : null,
                            return_tracking_number: (order.reverse_shipments && order.reverse_shipments[0]) ? order.reverse_shipments[0].return_tracking_number : null,
                        });
                    });

                    this.loading = false;
                });
        },
    },
    watch: {
        '$route': 'getData'
    },
};
</script>

<style scoped>
.fade-in-left {
    -webkit-animation: fade-in-left 1s cubic-bezier(0.445, 0.050, 0.550, 0.950) infinite ;
    animation: fade-in-left 1s cubic-bezier(0.445, 0.050, 0.550, 0.950) infinite ;
}

/* ----------------------------------------------
 * Generated by Animista on 2021-1-30 13:9:33
 * Licensed under FreeBSD License.
 * See http://animista.net/license for more info.
 * w: http://animista.net, t: @cssanimista
 * ---------------------------------------------- */

/**
 * ----------------------------------------
 * animation fade-in-top
 * ----------------------------------------
 */
@-webkit-keyframes fade-in-left {
    0% {
        -webkit-transform: translateX(-10px);
        transform: translateX(-10px);
        opacity: 0;
    }
    50% {
        opacity: 1;
    }
    100% {
        -webkit-transform: translateX(10);
        transform: translateX(10);
        opacity: 0;
    }
}
@keyframes fade-in-left {
    0% {
        -webkit-transform: translateX(-10px);
        transform: translateX(-10px);
        opacity: 0;
    }
    50% {
        opacity: 1;
    }
    100% {
        -webkit-transform: translateX(10);
        transform: translateX(10);
        opacity: 0;
    }
}

</style>
